import { IRequest } from 'legacy/lut/classes/models/onboarding/IRequest'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'

import WebSocketStore from 'core/classes/services/WebSocketStore'
import Logger from 'core/utils/Logger'
import {
  IChangeContactRequest,
  IChangePartnerRequest,
} from 'onboarding/features/models/progressDataModels'

export type TChangePartnerAPLParams = {
  requestId: number
  partnerId: string
  taxId?: string
  shouldCreateChangeRequest: boolean
}

class OnboardingRequestDetailApi {
  public getRequest = async (id: number) => {
    const response = await WebSocketStore.get('onboarding').call<{
      request: IRequest
    }>({
      service: 'request',
      action: 'getRequest',
      payload: { requestId: id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public getParentRequest = async (id: number) => {
    const response = await WebSocketStore.get('onboarding').call<{
      request: IRequest
    }>({
      service: 'request',
      action: 'getRequest',
      payload: { requestId: id },
      withoutTokenRefresh: true,
      isAuth: true,
    })

    if (response) {
      return response.data
    }
    return null
  }

  public getChildrenRequests = async (id: number) => {
    const response = await WebSocketStore.get('onboarding').call<{
      childrenRequests: IRequest[]
    }>({
      service: 'request',
      action: 'getChildrenRequests',
      payload: { requestId: id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public getRequestProcessesProgress = async (id: number) => {
    const response = await WebSocketStore.get('onboarding').call<{
      reasons: Record<string, IRequest>
    }>({
      service: 'request',
      action: 'getRequestProcessesProgress',
      payload: { requestId: id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public getRequestActionTransitions = async (id: number) => {
    const response = await WebSocketStore.get('onboarding').call<{
      reasons: Record<string, IRequest>
    }>({
      service: 'request',
      action: 'getRequestActionTransitions',
      payload: { requestId: id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  /**
   * todo: must be under onboarding/api/requestTemplateApi
   * @deprecated
   * @param id
   */
  public getRequestTemplate = async (id: string) => {
    const response = await WebSocketStore.get('onboarding').call<{
      reasons: Record<string, IRequest>
    }>({
      service: 'request-template',
      action: 'template-by-uuid',
      payload: { templateUUID: id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public getProfilingProgress = async (partnerId: string, requestId: number) => {
    const response = await WebSocketStore.get('onboarding').call<{
      changePartnerRequest: IChangePartnerRequest
      changeContactRequest: IChangeContactRequest[]
    }>({
      service: 'request',
      action: 'getProfilingProgress',
      payload: { partnerId, requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public async getLRProgress(requestId: number) {
    const response = await WebSocketStore.get('onboarding').call<
      Promise<{ approvalStatus: string; internalApprovalStatus: string }>
    >({
      service: 'request',
      action: 'getLRProgress',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public async getContractSignatureProgress(requestId: number) {
    const response = await WebSocketStore.get('onboarding').call<
      Promise<{ businessStatus: string; contractStatus: string | null }>
    >({
      service: 'request',
      action: 'getContractSignatureProgress',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public setRequestFieldIsEligible = async (requestId: number, value: boolean) => {
    let result
    try {
      result = await WebSocketStore.get('onboarding').call({
        service: 'apl',
        action: 'set-eligible',
        payload: {
          requestId: requestId,
          isEligible: value,
        },
      })
    } catch (e: any) {
      result = { success: false }
    }

    return result
  }

  public checkRequestFieldIsEligible = async (
    requestId: number
  ): Promise<{
    isEligible: boolean | null
  }> => {
    let result
    try {
      const response = await WebSocketStore.get('onboarding').call<{ isEligible: boolean }>({
        service: 'apl',
        action: 'check-eligible',
        payload: {
          requestId,
        },
      })
      result = response.data
    } catch (e: any) {
      result = { isEligible: null }
    }

    return result
  }

  public fetchSearchAttemptsCount = async (
    requestId: number
  ): Promise<{
    searchAttemptsCount: number
    remainingTime: number
  }> => {
    try {
      const response = await WebSocketStore.get('onboarding').call<{
        searchAttemptsCount: number
        remainingTime: number
      }>({
        service: 'apl',
        // todo: fix typo
        action: 'featch-search-attempts-count',
        payload: {
          requestId,
        },
      })
      return response.data
    } catch (e: any) {
      throw new Error(e.message)
    }
  }

  public incrementSearchAttemptsCount = async (
    requestId: number
  ): Promise<{
    searchAttemptsCount: number
    remainingTime: number
  }> => {
    try {
      const response = await WebSocketStore.get('onboarding').call<{
        searchAttemptsCount: number
        remainingTime: number
      }>({
        service: 'apl',
        action: 'increment-search-attempts-count',
        payload: {
          requestId,
        },
      })
      return response.data
    } catch (e: any) {
      throw new Error(e.message)
    }
  }

  public checkPartnerCanBeEligible = async (requestId: number) => {
    let result
    try {
      const response = await WebSocketStore.get('onboarding').call<{ inNonEligibleList: boolean }>({
        service: 'apl',
        action: 'check-in-non-eligible-list',
        payload: {
          requestId,
        },
      })
      result = response.data
    } catch (e: any) {
      result = { inNonEligibleList: null }
    }

    return result
  }

  public changePartnerAPL = async (params: TChangePartnerAPLParams) => {
    const response = await WebSocketStore.get('onboarding').call<{ changed: boolean }>({
      service: 'request',
      action: 'changePartnerAPL',
      payload: params,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message)
    }

    return response.data.changed
  }

  public searchAvailableRequestByEmail = async (
    requestType: RequestTypes,
    email: string
  ): Promise<number | null> => {
    let requestId = null
    try {
      const response = await WebSocketStore.get('onboarding').call<{ requestId: number }>({
        service: 'request',
        action: 'search-available-request-by-email',
        payload: {
          requestType: requestType,
          email: email,
        },
      })

      if (response.status === 'success') {
        requestId = response.data.requestId
      }
    } catch (e: any) {
      Logger.error(e.message)
    }

    return requestId
  }

  public getCaseOwnerSetDateInfo = async (id: number) => {
    try {
      const response = await WebSocketStore.get('onboarding').call<{
        caseOwnerSetDateInfo: Record<string, IRequest>
      }>({
        service: 'request',
        action: 'getCaseOwnerSetDateInfo',
        payload: { requestId: id },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return response.data.caseOwnerSetDateInfo
    } catch (e: any) {
      Logger.error(e.message)
    }
    return null
  }

  public async saveCaseOwnerSetDateInCollectedDataForm(requestId: number) {
    try {
      const response = await WebSocketStore.get('onboarding').call<{
        message: string
      }>({
        service: 'request',
        action: 'setCaseOwnerSetDate',
        payload: { requestId },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return response.data.message
    } catch (e: any) {
      Logger.error(e.message)
    }
    return null
  }
}

export const onboardingRequestDetailApi = new OnboardingRequestDetailApi()
